import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean, recoveryWidth: number | null }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Whether the cart drawer is temporarily squeezing the page and header.
   * Measurements taken in this state must not become responsive breakpoints.
   * @type {boolean}
   */
  #cartDrawerActive = false;

  /**
   * Header state captured before the cart starts affecting available width.
   * @type {'menu' | 'drawer' | null}
   */
  #menuStyleBeforeCart = null;

  /**
   * Measured width needed to restore the desktop menu before the cart opened.
   * @type {number | null}
   */
  #recoveryWidthBeforeCart = null;

  /**
   * Watches both the current theme-drawer implementation and the legacy
   * cart dialog for changes to their `open` attributes.
   */
  #cartDrawerObserver = new MutationObserver(() => {
    const isOpen = this.#isCartDrawerOpen();

    if (isOpen) {
      if (!this.#cartDrawerActive) {
        this.#menuStyleBeforeCart = this.dataset.menuStyle === 'drawer' ? 'drawer' : 'menu';
        this.#recoveryWidthBeforeCart = this.#menuDrawerHiddenWidth;
      }

      this.#cartDrawerActive = true;
      return;
    }

    if (!this.#cartDrawerActive) return;
    this.#cartDrawerActive = false;

    const shouldRemainDrawer =
      this.#menuStyleBeforeCart === 'drawer' &&
      (window.innerWidth < 750 ||
        this.#recoveryWidthBeforeCart === null ||
        window.innerWidth < this.#recoveryWidthBeforeCart);

    if (shouldRemainDrawer) {
      this.#menuDrawerHiddenWidth = this.#recoveryWidthBeforeCart;
      this.dataset.menuStyle = 'drawer';
      setHeaderMenuStyle();
    } else {
      this.#restoreMenuAfterCartClose();
    }

    this.#menuStyleBeforeCart = null;
    this.#recoveryWidthBeforeCart = null;
  });

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * RAF ID for restoring an overflow-triggered desktop drawer.
   * @type {number | null}
   */
  #menuResizeRafId = null;

  /**
   * Fallback reflow after the cart closing transition releases page width.
   * @type {number | null}
   */
  #cartRecoveryTimer = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    if (this.#cartDrawerActive || this.#isCartDrawerOpen()) return;
    if (window.innerWidth < 750) return;

    if (event.detail.minimumReached) {
      this.#updateMenuVisibility(true, event.detail.recoveryWidth);
    }
  };

  /**
   * Restore an overflow-triggered drawer after the viewport reaches the
   * measured recovery width. This listener is independent of header height,
   * so it also runs when a resize changes only the available inline space.
   */
  #handleMenuResize = () => {
    if (this.#menuResizeRafId !== null) return;

    this.#menuResizeRafId = requestAnimationFrame(() => {
      this.#menuResizeRafId = null;

      if (this.#cartDrawerActive || this.#isCartDrawerOpen()) return;

      const recoveryWidth = this.#menuDrawerHiddenWidth;
      const overflowList = this.refs.headerMenu?.querySelector('overflow-list');

      if (recoveryWidth !== null && window.innerWidth >= recoveryWidth) {
        overflowList?.removeAttribute('minimum-reached');
        this.#updateMenuVisibility(false);

        requestAnimationFrame(() => {
          overflowList?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
        });
        return;
      }

      if (this.dataset.menuStyle === 'menu') {
        overflowList?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
      }
    });
  };

  /**
   * Whether either supported cart drawer implementation is currently open.
   * @returns {boolean}
   */
  #isCartDrawerOpen() {
    const drawer = document.getElementById('cart-drawer') ?? document.querySelector('cart-drawer-component');
    const dialog = drawer?.matches('dialog') ? drawer : drawer?.querySelector('dialog');

    return Boolean(drawer?.hasAttribute('open') || dialog?.hasAttribute('open'));
  }

  /**
   * Observe the cart container and its dialog when present.
   */
  #observeCartDrawer() {
    const drawer = document.getElementById('cart-drawer') ?? document.querySelector('cart-drawer-component');
    if (!drawer) return;

    this.#cartDrawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });

    const dialog = drawer.matches('dialog') ? drawer : drawer.querySelector('dialog');
    if (dialog && dialog !== drawer) {
      this.#cartDrawerObserver.observe(dialog, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    this.#cartDrawerActive = this.#isCartDrawerOpen();
    if (this.#cartDrawerActive) {
      this.#menuStyleBeforeCart = this.dataset.menuStyle === 'drawer' ? 'drawer' : 'menu';
      this.#recoveryWidthBeforeCart = this.#menuDrawerHiddenWidth;
    }
  }

  /**
   * Discard the breakpoint captured while the page was squeezed, make the
   * desktop menu measurable again, then recalculate its real overflow.
   */
  #restoreMenuAfterCartClose() {
    const overflowList = this.refs.headerMenu?.querySelector('overflow-list');

    overflowList?.removeAttribute('minimum-reached');
    this.#updateMenuVisibility(false);

    if (this.#cartRecoveryTimer !== null) {
      clearTimeout(this.#cartRecoveryTimer);
      this.#cartRecoveryTimer = null;
    }

    const reflow = () => {
      overflowList?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reflow();

        requestAnimationFrame(() => {
          setHeaderMenuStyle();
        });

        this.#cartRecoveryTimer = window.setTimeout(() => {
          this.#cartRecoveryTimer = null;
          reflow();
          requestAnimationFrame(() => setHeaderMenuStyle());
        }, 220);
      });
    });
  }

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu, recoveryWidth = null) {
    if (hideMenu) {
      if (this.dataset.menuStyle === 'drawer') return;

      this.#menuDrawerHiddenWidth = recoveryWidth ?? window.innerWidth;
      this.dataset.menuStyle = 'drawer';
    } else {
      this.#menuDrawerHiddenWidth = null;
      this.dataset.menuStyle = 'menu';
    }

    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.addEventListener('resize', this.#handleMenuResize, { passive: true });
    this.#observeCartDrawer();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#cartDrawerObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('resize', this.#handleMenuResize);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#menuResizeRafId !== null) {
      cancelAnimationFrame(this.#menuResizeRafId);
      this.#menuResizeRafId = null;
    }
    if (this.#cartRecoveryTimer !== null) {
      clearTimeout(this.#cartRecoveryTimer);
      this.#cartRecoveryTimer = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
